import {
  runCrossWorkspaceFileDropWorkflow,
  runWorkspaceAddModeWorkflow,
} from "@/action-dialog";
import { useCopyFileRecord } from "@/api/fileRecords";
import { useFileConflictResolver } from "@/components/FileConflictResolver";
import { ConnectionsMenu } from "@/components/MessageInput/components/ConnectionsMenu";
import { ConnectorsMenuSection } from "@/components/MessageInput/components/ConnectorsMenuSection";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import {
  getViewportDocumentCollisionBoundary,
  VIEWPORT_POPOVER_COLLISION_PADDING,
} from "@/constants/viewportPopoverCollision";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { allowedExtensions } from "@/hooks/useFileRecordUpload";
import { WorkspaceFileCommandCascade } from "@/object-actions/renderers/WorkspaceFilePickerSubmenu";
import { cn } from "@/utils";
import analytics from "@/utils/analytics";
import type { FileRecord } from "@cohesive/api-client";
import { IntegrationConnection } from "@cohesive/api-client";
import {
  Entity,
  getEntityData,
  getEntityName,
  getEntityType,
  INSERT_CONNECTION_COMMAND,
} from "@cohesive/ui-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useRect } from "@radix-ui/react-use-rect";
import { AtSign, Upload } from "lucide-react";
import toast from "react-hot-toast";

interface AddMenuButtonProps {
  iconClassName?: string;
  /** Merged onto the @ trigger `Button` (e.g. compact canvas toolbar). */
  triggerButtonClassName?: string;

  onAttachFile?: (files: File[]) => void;
  /** Attach existing workspace files (live references — no copy). Enables the
   *  "Add from workspace" cascade together with `filesWorkspaceId`. */
  onAttachFileRecords?: (records: FileRecord[]) => void;
  /** The workspace whose files the picker browses. */
  filesWorkspaceId?: string;
  /** The chat a picked file can copy into. Absent (composer before the
   *  first send — no chat exists yet), picks attach the live reference
   *  directly instead of offering the reference-or-copy chooser. */
  flowSessionId?: string;
  /** Connectors matching the current `@` query. */
  filteredConnections: IntegrationConnection[];
  /** Any mentionable connector exists, ignoring the query. */
  hasConnections: boolean;
  connectionsLoading: boolean;
  connectionsError: boolean;
  /** The connectors section is available here — not that connectors exist. */
  connectionsEnabled: boolean;
  fileUploadEnabled: boolean;
  containerRect: ReturnType<typeof useRect>;
  addMenuOpen: boolean;
  setAddMenuOpen: (open: boolean) => void;
  showConnectionsInAddMenu: boolean;
  setShowConnectionsInAddMenu: (show: boolean) => void;
  activeConnection: IntegrationConnection | null;
  setActiveConnection: (connection: IntegrationConnection | null) => void;
  setMentionMode: (mode: "@" | "/" | null) => void;
  focusTextarea: () => void;
}

export const AddMenuButton: React.FC<AddMenuButtonProps> = ({
  iconClassName,
  triggerButtonClassName,

  onAttachFile,
  onAttachFileRecords,
  filesWorkspaceId,
  flowSessionId,
  filteredConnections,
  hasConnections,
  connectionsLoading,
  connectionsError,
  connectionsEnabled,
  fileUploadEnabled,
  containerRect = {
    left: 0,
    right: 0,
  },
  addMenuOpen,
  setAddMenuOpen,
  showConnectionsInAddMenu,
  setShowConnectionsInAddMenu,
  activeConnection,
  setActiveConnection,
  setMentionMode,
  focusTextarea,
}) => {
  const [editor] = useLexicalComposerContext();
  const { resolveCopies } = useFileConflictResolver();
  const copyFileRecord = useCopyFileRecord(undefined);
  const openSettings = useOpenSettings();

  /**
   * A workspace-file pick asks reference-or-copy — the same fork drops go through.
   * Reference attaches the workspace record itself; copy lands an independent
   * duplicate in this chat's files, then attaches that. Without a chat yet
   * there is nothing to copy into, so the pick attaches the reference
   * directly (the pre-chooser behavior).
   */
  const handleLibraryPick = (record: FileRecord) => {
    setAddMenuOpen(false);
    if (typeof onAttachFileRecords !== "function") return;
    if (!flowSessionId) {
      onAttachFileRecords([record]);
      return;
    }
    const chatFlowSessionId = flowSessionId;
    // Deferred past the closing menu so the chooser dialog doesn't fight
    // Radix's unmount focus handling.
    window.setTimeout(() => {
      void (async () => {
        const fileName = record.path.split("/").pop() || record.path;
        try {
          const choice =
            record.workspace_id &&
            filesWorkspaceId &&
            record.workspace_id !== filesWorkspaceId
              ? await runCrossWorkspaceFileDropWorkflow({
                  count: 1,
                  fileName,
                  destination: "chat",
                })
              : await runWorkspaceAddModeWorkflow({
                  count: 1,
                  fileName,
                  destination: "chat",
                });
          if (!choice) return;
          if (choice === "reference") {
            onAttachFileRecords([record]);
            return;
          }
          const ops = await resolveCopies({
            copies: [
              {
                fileRecordId: record.id,
                sourceName: fileName,
                isFolder: false,
                source: {
                  class: "workspace",
                  workspace_id: record.workspace_id,
                  path: record.path,
                },
                destination: {
                  components: {
                    class: "flow-session",
                    flow_session_id: chatFlowSessionId,
                  },
                  parentPath: "",
                },
              },
            ],
            sourceRecords: [record],
          });
          // Null or empty = the conflict prompt was cancelled/skipped.
          if (!ops || ops.length === 0) return;
          const copied = await copyFileRecord.mutateAsync({
            fileRecordId: ops[0].sourceFileRecordId,
            payload: ops[0].payload,
          });
          onAttachFileRecords([copied]);
        } catch {
          toast.error("Failed to add from the workspace");
        }
      })();
    }, 0);
  };

  const handleRecordSelect = (entity: Entity) => {
    try {
      const entityData = getEntityData(entity);
      // Generic entities fix `platform` to "generic" and carry the real
      // source platform in `data.platform_name` — prefer that so the mention
      // still identifies e.g. Zoom/Gmail rather than just "generic".
      const platform = entityData?.platform_name || entity.platform;
      const type = getEntityType(entity);
      const id = entityData?.id?.toString() || "";
      const name = getEntityName(entity);

      editor.dispatchCommand(INSERT_CONNECTION_COMMAND, {
        platform,
        type,
        identifier: id,
        title: name,
      });
      setActiveConnection(null);
      setShowConnectionsInAddMenu(false);
      setAddMenuOpen(false);
      setMentionMode(null);
      analytics.trackConnectorEntitySelected({
        platform,
        entityType: type,
        surface: "composer",
      });
      setTimeout(() => {
        focusTextarea();
      }, 0);
    } catch (error) {
      console.error("Failed to create connection from entity:", error);
      toast.error("Invalid entity format");
    }
  };

  const handleConnectionSelect = (connection: IntegrationConnection) => {
    setShowConnectionsInAddMenu(true);
    setActiveConnection(connection);
    // Reset mention mode when navigating to the entity selector
    setMentionMode(null);
  };

  const handleBrowseConnectors = () => {
    setAddMenuOpen(false);
    setShowConnectionsInAddMenu(false);
    setMentionMode(null);
    /** Defer so the Popover can finish unmounting; avoids body stuck with pointer-events: none (Radix #3317). */
    window.setTimeout(() => openSettings(ROUTES.SETTINGS.CONNECTORS), 0);
  };

  const handleAttach = () => {
    // Create a temporary file input to trigger file selection
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.style.display = "none";

    // Set accepted file extensions
    fileInput.accept = allowedExtensions.map((ext) => `.${ext}`).join(",");

    fileInput.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files) {
        onAttachFile?.(Array.from(target.files));
      }
      document.body.removeChild(fileInput);
    };

    document.body.appendChild(fileInput);
    fileInput.click();
  };

  const showUpload = fileUploadEnabled && typeof onAttachFile === "function";
  const showLibrary =
    !!filesWorkspaceId && typeof onAttachFileRecords === "function";

  return (
    <div className="z-9 order-2">
      <Popover
        open={addMenuOpen}
        onOpenChange={(open) => {
          setAddMenuOpen(open);
          if (!open) {
            focusTextarea();
          }
        }}
      >
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(
                  "text-muted-foreground hover:text-foreground transition-colors",
                  triggerButtonClassName,
                )}
              >
                <AtSign className={iconClassName} />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Add context '@'</TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          sideOffset={8}
          collisionBoundary={getViewportDocumentCollisionBoundary()}
          collisionPadding={VIEWPORT_POPOVER_COLLISION_PADDING}
          // Menu canon (matches DropdownMenuContent): p-1 rounded-md, compact
          // width. The connections view swaps to the wide entity selector.
          className={cn(
            "p-1 rounded-md",
            showConnectionsInAddMenu && activeConnection
              ? "compose-popover--wide"
              : "w-[260px] min-w-[260px]",
          )}
          style={
            showConnectionsInAddMenu && activeConnection && containerRect.width
              ? { width: Math.max(700, containerRect.width - 40) }
              : undefined
          }
          onCloseAutoFocus={(e) => {
            // Prevent popover from auto-focusing the trigger button on close.
            // This ensures focus returns to the textarea input instead.
            e.preventDefault();
          }}
        >
          {showConnectionsInAddMenu && activeConnection ? (
            <ConnectionsMenu
              activeConnection={activeConnection}
              setConnectionsMenuOpen={(open) => {
                setShowConnectionsInAddMenu(open);
                if (!open) {
                  setActiveConnection(null);
                  focusTextarea();
                }
              }}
              handleRecordSelect={handleRecordSelect}
            />
          ) : (
            <Command
              className="w-full overflow-hidden rounded-none bg-transparent"
              shouldFilter={false}
            >
              <CommandList className="max-h-[min(60vh,420px)] overflow-y-auto">
                {(showUpload || showLibrary) && (
                  <CommandGroup className="p-0">
                    {showUpload && (
                      <CommandItem
                        value="upload-file"
                        onSelect={() => {
                          setAddMenuOpen(false);
                          handleAttach();
                        }}
                      >
                        <Upload />
                        Upload File
                      </CommandItem>
                    )}
                    {showLibrary && (
                      // Nested pop: drill the workspace tree in place; a pick
                      // runs the reference-or-copy chooser, then attaches.
                      <WorkspaceFileCommandCascade
                        workspaceId={filesWorkspaceId}
                        onPick={handleLibraryPick}
                      />
                    )}
                  </CommandGroup>
                )}
                {connectionsEnabled && (
                  <>
                    {(showUpload || showLibrary) && <CommandSeparator />}
                    {/* Connectors — same section the canvas menus close on.
                        It stays mounted while loading or empty: the zero state
                        is where a user with none learns they exist. */}
                    <ConnectorsMenuSection
                      connections={filteredConnections}
                      hasConnections={hasConnections}
                      isLoading={connectionsLoading}
                      isError={connectionsError}
                      onSelectConnection={handleConnectionSelect}
                      onBrowseConnectors={handleBrowseConnectors}
                    />
                  </>
                )}
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
